import React, {useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import {
    Modelo200BreakdownStatus,
    Modelo200Data,
    Modelo200RequestRegimeEnum,
    Modelo202Data,
    Modelo202RequestModeEnum,
    Modelo202RequestRegimeEnum,
    TaxModelsRestControllerApiFactory,
} from "arteco-api-client-ts";
import {useApiManager} from "../../../../components/utils/api";
import {fmtAmt} from "../../../../components/utils/numbers";
import {btnSx, SoftChip} from "../../../../components/kit";
import {numSx} from "./labels";

const parse = (s: string) => s.trim() === "" ? undefined : Number(s.replace(",", "."));
const invalid = (v: number | undefined, allowNegative = false) => v !== undefined && (isNaN(v) || (!allowNegative && v < 0));
const pct = (r?: number) => r == null ? "—" : `${(r * 100).toLocaleString("es-ES", {maximumFractionDigits: 2})} %`;

const REGIMES = [
    {value: "", label: "Según la cifra de negocios del año anterior"},
    {value: "REDUCED_SIZE", label: "Reducida dimensión (art. 101, < 10 M€)"},
    {value: "MICRO", label: "Cifra de negocios < 1 M€ (escala)"},
    {value: "GENERAL", label: "General (25 %)"},
];

const REGIME_LABEL: Record<string, string> = {
    REDUCED_SIZE: "Reducida dimensión",
    MICRO: "Cifra de negocios < 1 M€",
    GENERAL: "General",
};

const Avisos = ({avisos}: {avisos?: string[]}) => (avisos?.length ?? 0) > 0 ? (
    <Alert severity="warning" sx={{mt: 2}}>
        {avisos!.map((a, i) => <div key={i}>{a}</div>)}
    </Alert>
) : null;

const Row = ({label, value, strong, color}: {label: string, value: React.ReactNode, strong?: boolean, color?: string}) => (
    <TableRow>
        <TableCell>{strong ? <strong>{label}</strong> : label}</TableCell>
        <TableCell align="right" sx={{...numSx, fontWeight: strong ? 700 : undefined, color}}>{value}</TableCell>
    </TableRow>
);

/**
 * Desglose de la cuota de un 200 ya presentado, copiado del justificante (#14729): los anteriores a la V132 no lo
 * guardaban y el 202 lo necesita. No cambia el estado, la referencia ni la cuota del 200.
 */
export const Completar200Dialog = ({year, open, onClose, onSaved}: {
    year: number, open: boolean, onClose: () => void, onSaved: () => void,
}) => {
    const apiManager = useApiManager();
    const taxCtrl = apiManager.factory(TaxModelsRestControllerApiFactory);
    const [grossTax, setGrossTax] = useState("");
    const [deductions, setDeductions] = useState("");
    const [withholdings, setWithholdings] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setGrossTax("");
            setDeductions("");
            setWithholdings("");
        }
    }, [open]);

    const gross = parse(grossTax), ded = parse(deductions), wh = parse(withholdings);
    const hasErrors = gross === undefined || invalid(gross) || invalid(ded) || invalid(wh);

    const save = () => {
        setSaving(true);
        apiManager.execute(taxCtrl.completeModelo200Breakdown(year, {grossTax: gross!, deductions: ded, withholdings: wh}))
            .then(() => {
                onSaved();
                onClose();
            })
            .catch(() => {})
            .finally(() => setSaving(false));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{sx: {borderRadius: 3}}}>
            <DialogTitle sx={{fontWeight: 700}}>Desglose del 200 de {year}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    Copia los importes del justificante del 200 presentado. El 202 parte de ellos; el estado, la referencia
                    y la cuota del 200 no cambian.
                </Typography>
                <Box sx={{display: "grid", gap: 2}}>
                    <TextField label="Cuota íntegra" size="small" required value={grossTax}
                               onChange={e => setGrossTax(e.target.value)} error={invalid(gross)}
                               inputProps={{inputMode: "decimal"}}
                               InputProps={{endAdornment: <InputAdornment position="end">€</InputAdornment>}}/>
                    <TextField label="Deducciones y bonificaciones" size="small" value={deductions}
                               onChange={e => setDeductions(e.target.value)} error={invalid(ded)}
                               inputProps={{inputMode: "decimal"}} helperText="Vacío es cero"
                               InputProps={{endAdornment: <InputAdornment position="end">€</InputAdornment>}}/>
                    <TextField label="Retenciones e ingresos a cuenta" size="small" value={withholdings}
                               onChange={e => setWithholdings(e.target.value)} error={invalid(wh)}
                               inputProps={{inputMode: "decimal"}} helperText="Vacío es cero"
                               InputProps={{endAdornment: <InputAdornment position="end">€</InputAdornment>}}/>
                </Box>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} sx={btnSx}>Cancelar</Button>
                <Button variant="contained" onClick={save} disabled={saving || hasErrors} sx={btnSx}>
                    {saving ? "Guardando..." : "Guardar desglose"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/** Modelo 200 (#14721): datos que no salen de la contabilidad y la liquidación con su desglose. */
export const Modelo200Dialog = ({year, open, onClose, onSaved}: {
    year: number, open: boolean, onClose: () => void, onSaved: () => void,
}) => {
    const apiManager = useApiManager();
    const taxCtrl = apiManager.factory(TaxModelsRestControllerApiFactory);
    const [adjustments, setAdjustments] = useState("");
    const [lossCompensation, setLossCompensation] = useState("");
    const [deductions, setDeductions] = useState("");
    const [regime, setRegime] = useState("");
    const [result, setResult] = useState<Modelo200Data | null>(null);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<Modelo200BreakdownStatus | null>(null);
    const [completing, setCompleting] = useState(false);

    const loadStatus = () => {
        apiManager.execute(taxCtrl.modelo200BreakdownStatus(year))
            .then(setStatus)
            .catch(() => setStatus(null));
    };

    useEffect(() => {
        if (open) {
            setResult(null);
            loadStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, year]);

    const adj = parse(adjustments), loss = parse(lossCompensation), ded = parse(deductions);
    const hasErrors = invalid(adj, true) || invalid(loss) || invalid(ded);

    // «Calcular» es la vista previa (#14733): no guarda nada; «Calcular y guardar» deja el registro como siempre.
    const calculate = (preview: boolean) => {
        setLoading(true);
        apiManager.execute(taxCtrl.generateModelo200(year, preview, {
            adjustments: adj,
            lossCompensation: loss,
            deductions: ded,
            regime: (regime || undefined) as Modelo200RequestRegimeEnum | undefined,
        }))
            .then(data => {
                setResult(data);
                if (!preview) onSaved();
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    const net = result?.netTax ?? 0;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{sx: {borderRadius: 3}}}>
            <DialogTitle sx={{fontWeight: 700}}>Modelo 200 — Impuesto de Sociedades {year}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    El resultado contable, las retenciones (473) y los pagos fraccionados salen de los datos. Lo demás, aquí.
                    «Calcular» no guarda nada; «Calcular y guardar» deja un solo registro por ejercicio y, si ya consta
                    presentado, no lo modifica.
                </Typography>
                {status?.presented && !status.hasBreakdown && (
                    <Alert severity="info" sx={{mb: 2}}
                           action={<Button color="inherit" size="small" onClick={() => setCompleting(true)}>
                               Completar con el justificante
                           </Button>}>
                        El 200 de {year} consta presentado{status.reference ? ` (${status.reference})` : ""}, pero sin el
                        desglose de la cuota que usa el 202. Cópialo del justificante.
                    </Alert>
                )}
                {status?.presented && status.hasBreakdown && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{mb: 2}}>
                        Presentado con cuota íntegra {fmtAmt(status.grossTax)} €, deducciones {fmtAmt(status.deductions)} € y
                        retenciones {fmtAmt(status.withholdings)} €.
                    </Typography>
                )}
                <Box sx={{display: "grid", gap: 2, gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}}}>
                    <TextField label="Ajustes extracontables" size="small" value={adjustments}
                               onChange={e => setAdjustments(e.target.value)} error={invalid(adj, true)}
                               inputProps={{inputMode: "decimal"}} helperText="Positivos o negativos"
                               InputProps={{endAdornment: <InputAdornment position="end">€</InputAdornment>}}/>
                    <TextField label="Bases negativas a compensar" size="small" value={lossCompensation}
                               onChange={e => setLossCompensation(e.target.value)} error={invalid(loss)}
                               inputProps={{inputMode: "decimal"}} helperText="De ejercicios anteriores"
                               InputProps={{endAdornment: <InputAdornment position="end">€</InputAdornment>}}/>
                    <TextField label="Deducciones y bonificaciones" size="small" value={deductions}
                               onChange={e => setDeductions(e.target.value)} error={invalid(ded)}
                               inputProps={{inputMode: "decimal"}}
                               InputProps={{endAdornment: <InputAdornment position="end">€</InputAdornment>}}/>
                    <TextField select label="Régimen" size="small" value={regime} onChange={e => setRegime(e.target.value)}>
                        {REGIMES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                    </TextField>
                </Box>

                {result && (
                    <>
                        <Table size="small" sx={{mt: 3}}>
                            <TableBody>
                                <Row label="Resultado contable antes de impuestos" value={`${fmtAmt(result.preTaxResult)} €`}/>
                                <Row label="Ajustes extracontables" value={`${fmtAmt(result.adjustments)} €`}/>
                                <Row label="Compensación de bases negativas" value={`- ${fmtAmt(result.lossCompensation)} €`}/>
                                <Row label="Base imponible" value={`${fmtAmt(result.taxBase)} €`} strong/>
                                <Row label={`Tipo (${REGIME_LABEL[result.regime ?? ""] ?? result.regime})`}
                                     value={result.firstBracketRate != null && result.firstBracketRate !== result.taxRate
                                         ? `${pct(result.firstBracketRate)} / ${pct(result.taxRate)}` : pct(result.taxRate)}/>
                                <Row label="Cuota íntegra" value={`${fmtAmt(result.grossTax)} €`}/>
                                <Row label="Deducciones y bonificaciones" value={`- ${fmtAmt(result.deductions)} €`}/>
                                <Row label="Retenciones e ingresos a cuenta (473)" value={`- ${fmtAmt(result.withholdings)} €`}/>
                                <Row label="Pagos fraccionados (202)" value={`- ${fmtAmt(result.installmentsPaid)} €`}/>
                                <Row label={net >= 0 ? "Cuota diferencial a ingresar" : "Cuota diferencial a devolver"}
                                     value={`${fmtAmt(result.netTax)} €`} strong color={net < 0 ? "success.main" : undefined}/>
                            </TableBody>
                        </Table>
                        <Avisos avisos={result.avisos}/>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} sx={btnSx}>Cerrar</Button>
                <Button variant="outlined" onClick={() => calculate(true)} disabled={loading || hasErrors} sx={btnSx}>
                    Calcular
                </Button>
                <Button variant="contained" onClick={() => calculate(false)} disabled={loading || hasErrors} sx={btnSx}>
                    {loading ? "Calculando..." : "Calcular y guardar"}
                </Button>
            </DialogActions>
            <Completar200Dialog year={year} open={completing} onClose={() => setCompleting(false)}
                                onSaved={() => {
                                    loadStatus();
                                    onSaved();
                                }}/>
        </Dialog>
    );
};

/** Modelo 202 (#14721): los tres pagos fraccionados del ejercicio, por el art. 40.2 o el 40.3. */
export const Modelo202Dialog = ({year, open, onClose, onSaved}: {
    year: number, open: boolean, onClose: () => void, onSaved: () => void,
}) => {
    const apiManager = useApiManager();
    const taxCtrl = apiManager.factory(TaxModelsRestControllerApiFactory);
    const [mode, setMode] = useState("ART_40_2");
    const [adjustments, setAdjustments] = useState("");
    const [lossCompensation, setLossCompensation] = useState("");
    const [regime, setRegime] = useState("");
    const [result, setResult] = useState<Modelo202Data | null>(null);
    const [loading, setLoading] = useState(false);
    const [completeYear, setCompleteYear] = useState<number | null>(null);
    const [previewed, setPreviewed] = useState(false);

    useEffect(() => {
        if (open) setResult(null);
    }, [open]);

    // Ejercicios del 200 sin desglose sobre los que se ha estimado algún pago (#14729)
    const estimatedYears = Array.from(new Set((result?.installments ?? [])
        .filter(i => i.estimated && i.baseYear != null).map(i => i.baseYear as number)));
    // De esos, los presentados: solo esos se completan con el justificante; un 200 pendiente se recalcula
    const [presentedYears, setPresentedYears] = useState<number[]>([]);
    const estimatedKey = estimatedYears.join(",");

    useEffect(() => {
        setPresentedYears([]);
        estimatedYears.forEach(y => apiManager.execute(taxCtrl.modelo200BreakdownStatus(y))
            .then(st => {
                if (st.presented && !st.hasBreakdown) setPresentedYears(prev => prev.includes(y) ? prev : [...prev, y]);
            })
            .catch(() => {}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estimatedKey]);

    const is403 = mode === "ART_40_3";
    const adj = parse(adjustments), loss = parse(lossCompensation);
    const hasErrors = is403 && (invalid(adj, true) || invalid(loss));

    // «Calcular» es la vista previa (#14733): no guarda ni da de baja nada.
    const calculate = (preview = false) => {
        setLoading(true);
        apiManager.execute(taxCtrl.modelo202(year, preview, {
            mode: mode as Modelo202RequestModeEnum,
            adjustments: is403 ? adj : undefined,
            lossCompensation: is403 ? loss : undefined,
            regime: (is403 && regime ? regime : undefined) as Modelo202RequestRegimeEnum | undefined,
        }))
            .then(data => {
                setResult(data);
                setPreviewed(preview);
                if (!preview) onSaved();
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{sx: {borderRadius: 3}}}>
            <DialogTitle sx={{fontWeight: 700}}>Modelo 202 — Pagos fraccionados {year}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    Un registro por periodo (abril, octubre y diciembre): «Calcular y guardar» actualiza los pendientes y no toca
                    los presentados; «Calcular» no guarda nada.
                </Typography>
                <Box sx={{display: "grid", gap: 2, gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}}}>
                    <TextField select label="Modalidad" size="small" value={mode} onChange={e => setMode(e.target.value)}
                               sx={{gridColumn: {sm: "1 / -1"}}}
                               helperText={is403
                                   ? "5/7 del tipo sobre la base de los 3, 9 u 11 primeros meses, menos retenciones y pagos anteriores"
                                   : "18 % de la cuota del último 200 vencido, menos deducciones, bonificaciones y retenciones"}>
                        <MenuItem value="ART_40_2">Art. 40.2 — sobre la cuota del último 200</MenuItem>
                        <MenuItem value="ART_40_3">Art. 40.3 — sobre la base del periodo</MenuItem>
                    </TextField>
                    {is403 && (
                        <>
                            <TextField label="Ajustes extracontables" size="small" value={adjustments}
                                       onChange={e => setAdjustments(e.target.value)} error={invalid(adj, true)}
                                       inputProps={{inputMode: "decimal"}} helperText="Positivos o negativos"
                                       InputProps={{endAdornment: <InputAdornment position="end">€</InputAdornment>}}/>
                            <TextField label="Bases negativas a compensar" size="small" value={lossCompensation}
                                       onChange={e => setLossCompensation(e.target.value)} error={invalid(loss)}
                                       inputProps={{inputMode: "decimal"}}
                                       InputProps={{endAdornment: <InputAdornment position="end">€</InputAdornment>}}/>
                            <TextField select label="Régimen" size="small" value={regime} onChange={e => setRegime(e.target.value)}
                                       sx={{gridColumn: {sm: "1 / -1"}}}>
                                {REGIMES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                            </TextField>
                        </>
                    )}
                </Box>

                {result && (
                    <>
                        {/* #14729: a 390 px se desplaza en horizontal y la columna Estado no se corta */}
                        <TableContainer sx={{overflowX: "auto", mt: 3}}>
                            <Table size="small" sx={{minWidth: 600}}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Periodo</TableCell>
                                        {!is403 && <TableCell>Sobre el 200 de</TableCell>}
                                        <TableCell align="right">Base</TableCell>
                                        <TableCell align="right">Porcentaje</TableCell>
                                        <TableCell align="right">A ingresar</TableCell>
                                        <TableCell>Estado</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(result.installments ?? []).map(i => (
                                        <TableRow key={i.period}>
                                            <TableCell>{i.period}</TableCell>
                                            {!is403 && <TableCell sx={numSx}>{i.baseYear ?? "—"}</TableCell>}
                                            <TableCell align="right" sx={numSx}>{i.base != null ? `${fmtAmt(i.base)} €` : "—"}</TableCell>
                                            <TableCell align="right" sx={numSx}>{pct(i.rate)}</TableCell>
                                            <TableCell align="right" sx={{...numSx, fontWeight: 600}}>
                                                {i.amount != null ? `${fmtAmt(i.amount)} €` : "—"}
                                            </TableCell>
                                            <TableCell sx={{whiteSpace: "nowrap"}}>
                                                {i.locked
                                                    ? <SoftChip label="Presentado" tone="success"/>
                                                    : i.estimated ? <SoftChip label="Estimación, sin guardar" tone="warning"/>
                                                        : i.submission ? <SoftChip label="Guardado" tone="info"/>
                                                        : previewed && i.amount != null ? <SoftChip label="Sin guardar" tone="neutral"/>
                                                            : <SoftChip label="Sin datos" tone="warning"/>}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell colSpan={is403 ? 3 : 4}>
                                            <strong>Total del ejercicio</strong>
                                            {estimatedYears.length > 0 && (
                                                <Typography variant="caption" color="text.secondary" component="div">
                                                    Sin las estimaciones
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right" sx={{...numSx, fontWeight: 700}}>{fmtAmt(result.total)} €</TableCell>
                                        <TableCell/>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Avisos avisos={result.avisos}/>
                        {presentedYears.map(y => (
                            <Alert key={y} severity="info" sx={{mt: 2}}
                                   action={<Button color="inherit" size="small" onClick={() => setCompleteYear(y)}>
                                       Completar con el justificante
                                   </Button>}>
                                El 200 de {y} no tiene el desglose de la cuota.
                            </Alert>
                        ))}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} sx={btnSx}>Cerrar</Button>
                <Button variant="outlined" onClick={() => calculate(true)} disabled={loading || hasErrors} sx={btnSx}>
                    Calcular
                </Button>
                <Button variant="contained" onClick={() => calculate(false)} disabled={loading || hasErrors} sx={btnSx}>
                    {loading ? "Calculando..." : "Calcular y guardar"}
                </Button>
            </DialogActions>
            <Completar200Dialog year={completeYear ?? year} open={completeYear != null} onClose={() => setCompleteYear(null)}
                                onSaved={() => calculate(previewed)}/>
        </Dialog>
    );
};
